import { Alert, Button, Layout, LayoutWidth, Loader } from '@lifebit-ai/lds'
import React from 'react'

import ApprovalModal from 'components/ApprovalModal/ApprovalModal'
import RejectionModal from 'components/RejectionModal/RejectionModal'
import { LoadingWrapper } from 'core/Common.styles'
import NotFound from 'modules/general/not-found/NotFound'
import { ExternalImportRequestDetailsContent } from 'modules/requests/ImportRequestDetails/ExternalImportRequestDetailsContent'
import { RequestActivityCard } from 'modules/requests/components/RequestActivity/RequestActivityCard'
import { RequestDetailLayout } from 'modules/requests/components/RequestDetailLayout/RequestDetailLayout'
import { RequestLabelsCard } from 'modules/requests/components/RequestLabelsCard/RequestLabelsCard'
import { AlertContainer } from 'modules/resultsV2/Results.styles'
import FileDetails from 'modules/resultsV2/components/FileDetails/FileDetails'
import FormSubmissionPreview from 'modules/resultsV2/components/FormSubmissionPreview/FormSubmissionPreview'
import ViewFormModal from 'modules/resultsV2/components/ViewFormModal/ViewFormModal'
import { APPROVAL_ALERT_TEST_ID, APPROVE_BUTTON_TEST_ID, REJECT_BUTTON_TEST_ID } from 'modules/resultsV2/utils'
import { isUploadedImportRequestDetail } from 'services/import-requests/importRequestUtils'
import { toImportRequestActivitySource } from 'services/request-activity/mappers'
import { CardBody, RequestCard } from 'ui/card/card.styles'

import * as styles from './ImportRequestDetails.styles'
import { ImportRequestDetailsMetadata } from './ImportRequestDetailsMetadata'
import { ImportRequestDetailsTags } from './ImportRequestDetailsTags'
import { ImportRequestFailureAlert } from './ImportRequestFailureAlert'
import { ImportRequestScanCard } from './ImportRequestScanCard'
import { useImportRequestDetails } from './hooks/useImportRequestDetails'

export const IMPORT_REQUEST_DETAILS_LOADER_TEST_ID = 'import-request-details-loader'

export const ImportRequestDetails = (): JSX.Element => {
  const {
    approvalModalDescription,
    approveRequest,
    approveRequestMutation,
    closeApprovalModal,
    closeRejectionModal,
    closeViewFormModal,
    formattedFileSize,
    goBack,
    handleAddResultLabel,
    handleDeleteResultLabel,
    handleOnClickLabelMenuItem,
    importRequest,
    isApprovalActionsVisible,
    isApprovalRestricted,
    isApprovalModalOpen,
    isFailureAlertVisible,
    isLoading,
    isNotFound,
    isRejectionModalOpen,
    isViewFormModalOpen,
    labelsQuery,
    openApprovalModal,
    openRejectionModal,
    openViewFormModal,
    pageTitle,
    rejectRequest,
    rejectRequestMutation,
    rejectionModalDescription,
    validateResultLabel
  } = useImportRequestDetails()

  if (isLoading) {
    return (
      <Layout layoutWidth={LayoutWidth.XLarge}>
        <styles.StyledContent>
          <LoadingWrapper data-testid={IMPORT_REQUEST_DETAILS_LOADER_TEST_ID}>
            <Loader />
          </LoadingWrapper>
        </styles.StyledContent>
      </Layout>
    )
  }

  if (isNotFound || !importRequest) {
    return <NotFound />
  }

  return (
    <Layout layoutWidth={LayoutWidth.XLarge}>
      <Layout.Header
        hasSeparator={false}
        onClickGoBack={goBack}
        subtitle={<ImportRequestDetailsTags importRequest={importRequest} />}
        title={pageTitle}
        actions={
          isApprovalActionsVisible ? (
            <styles.ButtonsWrapper>
              <Button
                data-testid={REJECT_BUTTON_TEST_ID}
                type="secondary"
                onClick={openRejectionModal}
                loading={rejectRequestMutation.isPending}
                danger
              >
                Reject
              </Button>
              <Button data-testid={APPROVE_BUTTON_TEST_ID} type="primary" onClick={openApprovalModal}>
                Approve
              </Button>
            </styles.ButtonsWrapper>
          ) : undefined
        }
      />

      <Layout.Separator />

      <styles.StyledContent>
        <RequestDetailLayout
          aside={
            <>
              <RequestLabelsCard
                addedLabels={importRequest.labels}
                allLabels={labelsQuery.data?.data}
                onAddResultLabel={handleAddResultLabel}
                onValidateResultLabel={validateResultLabel}
                onClickLabelMenuItem={handleOnClickLabelMenuItem}
                onDeleteResultLabel={handleDeleteResultLabel}
              />
              <ImportRequestScanCard importRequest={importRequest} />
              <RequestActivityCard source={toImportRequestActivitySource(importRequest)} />
            </>
          }
        >
          {isApprovalRestricted && (
            <AlertContainer>
              <Alert type="info" data-testid={APPROVAL_ALERT_TEST_ID}>
                This import request is from a workspace with approval restrictions enabled. Since you submitted the
                request, you cannot approve or reject it. Another Airlock Manager will need to review it.
              </Alert>
            </AlertContainer>
          )}

          {isFailureAlertVisible && <ImportRequestFailureAlert importRequest={importRequest} />}

          {/* One card: what came in, how far it has got, then the facts about the request. */}
          <RequestCard>
            <CardBody>
              {isUploadedImportRequestDetail(importRequest) ? (
                <>
                  <FileDetails
                    displayName={importRequest.fileName}
                    iconTestId="import-request-file-icon"
                    isFile
                    subtitle={formattedFileSize}
                  />

                  {importRequest.formSubmission ? (
                    <FormSubmissionPreview
                      formSubmission={importRequest.formSubmission}
                      onViewForm={openViewFormModal}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  <FileDetails
                    displayName={importRequest.sourceUri}
                    iconTestId="external-import-source-icon"
                    isFile={false}
                    subtitle="External Source Cloud Storage Location"
                  />

                  <ExternalImportRequestDetailsContent importRequest={importRequest} />
                </>
              )}

              <ImportRequestDetailsMetadata importRequest={importRequest} />
            </CardBody>
          </RequestCard>
        </RequestDetailLayout>
      </styles.StyledContent>

      {isUploadedImportRequestDetail(importRequest) ? (
        <ViewFormModal
          open={isViewFormModalOpen}
          onClose={closeViewFormModal}
          formSubmission={importRequest.formSubmission}
          resultId={importRequest._id}
        />
      ) : null}
      <ApprovalModal
        action={approveRequest}
        description={approvalModalDescription}
        isError={approveRequestMutation.isError}
        isLoading={approveRequestMutation.isPending}
        isOpen={isApprovalModalOpen}
        okTitle="Yes, approve"
        onClose={closeApprovalModal}
        title="Approve data import request?"
      />
      <RejectionModal
        action={rejectRequest}
        description={rejectionModalDescription}
        isError={rejectRequestMutation.isError}
        isLoading={rejectRequestMutation.isPending}
        isOpen={isRejectionModalOpen}
        okTitle="Reject request"
        onClose={closeRejectionModal}
        title="Reject data import request?"
      />
    </Layout>
  )
}

export default ImportRequestDetails
